import { useQuery } from '@tanstack/react-query';
import {
  alertsApi,
  inspectNatsApi,
  inspectRedisApi,
  notificationsApi,
  runsApi,
  workflowsApi,
  type NatsPeekQuery,
  type RedisScanQuery,
  type RunQuery,
} from '@/lib/apiClient';

export const keys = {
  workflows: ['workflows'] as const,
  workflow: (slug: string) => ['workflows', slug] as const,
  workflowRuns: (slug: string, query: RunQuery) => ['workflows', slug, 'runs', query] as const,
  runs: (query: RunQuery) => ['runs', query] as const,
  run: (id: string) => ['runs', id] as const,
  runEvents: (id: string) => ['runs', id, 'events'] as const,
  alerts: ['alerts'] as const,
  rules: ['notifications', 'rules'] as const,
  channels: ['notifications', 'channels'] as const,
  noiseBudget: ['notifications', 'noise-budget'] as const,
  natsOverview: ['inspect', 'nats'] as const,
  natsStream: (name: string) => ['inspect', 'nats', name] as const,
  natsMessages: (name: string, query: NatsPeekQuery) =>
    ['inspect', 'nats', name, 'messages', query] as const,
  redisOverview: ['inspect', 'redis'] as const,
  redisKeys: (query: RedisScanQuery) => ['inspect', 'redis', 'keys', query] as const,
  redisValue: (key: string, db?: number) => ['inspect', 'redis', 'value', key, db] as const,
};

export function useWorkflows() {
  return useQuery({ queryKey: keys.workflows, queryFn: workflowsApi.list });
}

export function useWorkflow(slug: string) {
  return useQuery({ queryKey: keys.workflow(slug), queryFn: () => workflowsApi.get(slug), enabled: !!slug });
}

export function useWorkflowRuns(slug: string, query: RunQuery = {}) {
  return useQuery({
    queryKey: keys.workflowRuns(slug, query),
    queryFn: () => workflowsApi.runs(slug, query),
    enabled: !!slug,
  });
}

export function useRuns(query: RunQuery = {}) {
  return useQuery({ queryKey: keys.runs(query), queryFn: () => runsApi.list(query) });
}

export function useRun(id: string) {
  return useQuery({
    queryKey: keys.run(id),
    queryFn: () => runsApi.get(id),
    enabled: !!id,
    refetchInterval: (query) => (query.state.data?.status === 'running' ? 5_000 : false),
  });
}

export function useRunEvents(id: string, isLive: boolean) {
  return useQuery({
    queryKey: keys.runEvents(id),
    queryFn: () => runsApi.events(id),
    enabled: !!id,
    refetchInterval: isLive ? 5_000 : false,
  });
}

export function useAlerts() {
  return useQuery({ queryKey: keys.alerts, queryFn: alertsApi.list });
}

export function useRules() {
  return useQuery({ queryKey: keys.rules, queryFn: notificationsApi.rules });
}

export function useChannels() {
  return useQuery({ queryKey: keys.channels, queryFn: notificationsApi.channels });
}

export function useNoiseBudget() {
  return useQuery({ queryKey: keys.noiseBudget, queryFn: notificationsApi.noiseBudget });
}

export function useNatsOverview() {
  return useQuery({
    queryKey: keys.natsOverview,
    queryFn: inspectNatsApi.overview,
    refetchInterval: 15_000,
  });
}

export function useNatsStream(name: string) {
  return useQuery({
    queryKey: keys.natsStream(name),
    queryFn: () => inspectNatsApi.stream(name),
    enabled: !!name,
  });
}

export function useNatsMessages(name: string, query: NatsPeekQuery, enabled: boolean) {
  return useQuery({
    queryKey: keys.natsMessages(name, query),
    queryFn: () => inspectNatsApi.messages(name, query),
    enabled: enabled && !!name,
    staleTime: 0,
  });
}

export function useRedisOverview() {
  return useQuery({
    queryKey: keys.redisOverview,
    queryFn: inspectRedisApi.overview,
    refetchInterval: 15_000,
  });
}

export function useRedisKeys(query: RedisScanQuery, enabled: boolean) {
  return useQuery({
    queryKey: keys.redisKeys(query),
    queryFn: () => inspectRedisApi.keys(query),
    enabled,
    staleTime: 0,
  });
}

export function useRedisValue(key: string, db: number | undefined, enabled: boolean) {
  return useQuery({
    queryKey: keys.redisValue(key, db),
    queryFn: () => inspectRedisApi.value(key, db),
    enabled: enabled && !!key,
    staleTime: 0,
  });
}
